import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  CircularProgress,
} from "@material-ui/core";
import {
  FETCH_PRESHIFT_QUESTIONS,
  FETCH_PRESHIFT_QUESTIONS_SUCCESS,
  FETCH_PRESHIFT_QUESTIONS_FAILURE,
  ADD_PRESHIFT_QUESTION,
  ADD_PRESHIFT_QUESTION_SUCCESS,
  ADD_PRESHIFT_QUESTION_FAILURE,
  EDIT_PRESHIFT_QUESTION,
  EDIT_PRESHIFT_QUESTION_SUCCESS,
  EDIT_PRESHIFT_QUESTION_FAILURE,
  DELETE_PRESHIFT_QUESTION,
  DELETE_PRESHIFT_QUESTION_SUCCESS,
  DELETE_PRESHIFT_QUESTION_FAILURE,
  UPDATE_PRESHIFT_QUESTION_ORDER,
  UPDATE_PRESHIFT_QUESTION_ORDER_SUCCESS,
  UPDATE_PRESHIFT_QUESTION_ORDER_FAILURE,
  SAVE_PRESHIFT_QUESTION_CHANGES,
  SAVE_PRESHIFT_QUESTION_CHANGES_SUCCESS,
  SAVE_PRESHIFT_QUESTION_CHANGES_FAILURE,
  DISCARD_PRESHIFT_QUESTION_CHANGES,
  DISCARD_PRESHIFT_QUESTION_CHANGES_SUCCESS,
  DISCARD_PRESHIFT_QUESTION_CHANGES_FAILURE,
} from "@utils/actionTypes";
import { toast } from "react-toastify";
import API from "@services/axios";
import { schema } from "@utils/schemas";
import validationSchema from "@utils/validationSchemas";
import { useStore } from "@store/store";
import closeIcon from "@assets/images/close.svg";
import { PreshiftPopupStyle } from "./style";
import { useDebouncedEffect } from "@hooks/debounceEffect";
import SearchIcon from "@assets/images/search.svg";

import PreshiftTable from "./table-listing";
import AddEditPreshiftPopup from "../AddEditPreshiftPopup";
import CustomDeletePopup from "@components/customDeletePopup";
import CustomConfirmationPopup from "@components/customConfirmationPopup";

function PreshiftPopup(props) {
  const classes = PreshiftPopupStyle();
  const [state, dispatch] = useStore();
  const [scroll] = useState("body");
  const [error, setError] = useState("");
  const [clonedPreshiftQuestions, setClonedPreshiftQuestions] = useState([]);

  // Delete Question Popup
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [deletedQuestionData, setDeletedQuestionData] = useState(null);

  // Add/Edit Question Popup
  const [openAddEditQuestionPopup, setOpenAddEditQuestionPopup] = useState(false);
  const [addEditQuestionData, setAddEditQuestionData] = useState(schema.addPreshiftQuestionSchema);

  // Add/Edit Question Mode
  const [isEditMode, setEditMode] = useState(false);

  // Drag/Drop Questions
  const [dragIndex, setDragIndex] = useState(null);
  const [originalDragIndex, setOriginalDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const draggingEnabled = useRef(false);

  // Save Changes Confirmation
  const [openSaveChangeConfirmPopup, setOpenSaveChangeConfirmPopup] = useState(false);
  const [openSaveDiscardChangePopup, setOpenSaveDiscardChangePopup] = useState(false);

  /* Preshift Questions */
  let getListOfPreshiftQuestions = (search = "") => {
    const params = {
      ...(!!search ? { search: search } : {}),
      order: props.order !== "" ? props.order : "asc",
      orderBy: props.orderBy !== "" ? props.orderBy : "displayOrder",
    };
    dispatch({ type: FETCH_PRESHIFT_QUESTIONS });
    API.get("drivers/preshiftQuestions", { params })
      .then((response) => {
        dispatch({
          type: FETCH_PRESHIFT_QUESTIONS_SUCCESS,
          payload: response?.data?.data?.preshiftQuestions,
        });
        props.setPreshiftQuestions(response?.data?.data?.preshiftQuestions);
        setClonedPreshiftQuestions(response?.data?.data?.preshiftQuestions);
        props.setSavedChangeEnabled(response?.data?.data?.isSavedChangeEnabled);
      })
      .catch((err) => {
        dispatch({ type: FETCH_PRESHIFT_QUESTIONS_FAILURE, payload: err });
      });
  };
  
  useEffect(() => {
    if (props.open) {
      getListOfPreshiftQuestions(props.search);
    }
  }, [props.open]);

  useEffect(() => {
    getListOfPreshiftQuestions(props.search);
    props?.setDetectedChanges(false);
  }, [props.orderBy, props.order]);

  useEffect(() => {
    if (props.isSavedChangeEnabled && props?.detectedChanges) {
      setError("");
      setOpenSaveDiscardChangePopup(true);
      props?.setOpen(true);
    }
  }, [props?.detectedChanges, props.isSavedChangeEnabled]);

  useDebouncedEffect(() => {
    getListOfPreshiftQuestions(props.search);
    props?.setDetectedChanges(false);
  }, 1000, [props.search]);

  const handleSearch = (event) => {
    props.setSearch(event.target.value.trimStart());
  };
  const handleSorting = (event, property) => {
    const isAsc = props.orderBy === property.sortTitle && props.order === "asc";
    props.setOrder(isAsc ? "desc" : "asc");
    props.setOrderBy(property.sortTitle);
  };

  /* Add/Edit Preshift Questions */
  const handleAddEditQuestionPopup = (question = null) => {
    formik.handleReset();
    setAddEditQuestionData(schema.addPreshiftQuestionSchema);
    if (question) {
      setEditMode(true);
      const data = {
        id: question?.id,
        originalQuestionText: question?.questionTxt?.replace(/\s+/g, " ").trim(),
        questionText: question?.questionTxt?.replace(/\s+/g, " ").trim(),
      }
      setAddEditQuestionData(data);
    } else {
      setEditMode(false);
    }
    setOpenAddEditQuestionPopup(true);
    setError("");
  };

  const handleCloseAddEditQuestionModal = () => {
    setOpenAddEditQuestionPopup(false);
    setError("");
  };

  // API calling to add Preshift Question
  let addPreshiftQuestion = (value) => {
    const data = {
      questionTxt: value?.questionText?.replace(/\s+/g, " ").trim(),
    };
    dispatch({ type: ADD_PRESHIFT_QUESTION });
    API.post("drivers/preshiftQuestions", data)
      .then((response) => {
        dispatch({
          type: ADD_PRESHIFT_QUESTION_SUCCESS,
        });
        props?.setDetectedChanges(false);
        toast.success(response?.data?.message);
        handleCloseAddEditQuestionModal();
        getListOfPreshiftQuestions(props.search);
      })
      .catch((error) => {
        dispatch({ type: ADD_PRESHIFT_QUESTION_FAILURE });
        setError(error?.response?.data?.message);
      });
  };

  // API calling to edit preshift question
  let editPreshiftQuestion = (value) => {
    const data = {
      id: value?.id,
      questionTxt: value?.questionText?.replace(/\s+/g, " ").trim(),
    };
    dispatch({ type: EDIT_PRESHIFT_QUESTION });
    API.put(`drivers/preshiftQuestions/${value?.id}`, data)
      .then((response) => {
        dispatch({
          type: EDIT_PRESHIFT_QUESTION_SUCCESS,
        });
        props?.setDetectedChanges(false);
        toast.success(response?.data?.message);
        handleCloseAddEditQuestionModal();
        getListOfPreshiftQuestions(props.search);
      })
      .catch((error) => {
        dispatch({ type: EDIT_PRESHIFT_QUESTION_FAILURE});
        setError(error?.response?.data?.message);
      });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: addEditQuestionData,
    validationSchema: validationSchema.preshiftQuestionValidationSchema,
    onSubmit: (value) => {
      if (isEditMode) {
        editPreshiftQuestion(value);
      } else {
        addPreshiftQuestion(value);
      }
    },
  });

  /* Delete Preshift Questions */
  const handleDeleteQuestionPopup = (question) => {
    setDeletedQuestionData(question);
    setOpenDeletePopup(true);
  };

  const handleDeleteQuestion = (question) => {
    dispatch({ type: DELETE_PRESHIFT_QUESTION });
    API.delete(`drivers/preshiftQuestions/${question?.id}`)
      .then((response) => {
        dispatch({
          type: DELETE_PRESHIFT_QUESTION_SUCCESS,
        });
        props?.setDetectedChanges(false);
        toast.success(response?.data?.message);
        setOpenDeletePopup(false);
        getListOfPreshiftQuestions(props.search);
        
      })
      .catch((error) => {
        setError("You can't delete this record");
        dispatch({ type: DELETE_PRESHIFT_QUESTION_FAILURE});
      });
  };

  /* Drag and Drop Questions */
  // Begin Drag Item
  const handleDragStart = (index, e) => {
    if (!draggingEnabled.current) {
      e.preventDefault();
      return;
    }
    setOriginalDragIndex(index);
    setDragIndex(index);
  };

  // Drag over Item
  const handleDragOver = (index, event) => {
    event.preventDefault();

    if (dragIndex === null) return;
    if (dragIndex === index) return;

    const updated = [...props.preshiftQuestions];

    // Get Item being dragged
    const draggedItem = updated[dragIndex];
    updated.splice(dragIndex, 1);
    updated.splice(index, 0, draggedItem);

    props.setPreshiftQuestions(updated);
    setDragIndex(index);
  };

  // Drop item
  const handleDrop = (currentDraggedId, dropIndex) => {
    if (dragIndex === null) return;
    if (originalDragIndex === null) return;

    // Clone Preshift question list
    const cloneUpdatedQuestions = [...clonedPreshiftQuestions];
    const draggedItem = cloneUpdatedQuestions[originalDragIndex];
    const targetItem = cloneUpdatedQuestions[dropIndex];

    // Get Item being dragged
    cloneUpdatedQuestions.splice(originalDragIndex, 1);
    cloneUpdatedQuestions.splice(dropIndex, 0, draggedItem);

    // Drag/drop item UI in real preshift question list
    const updatedQuestions = [...props.preshiftQuestions];
    const draggedUpdatedItem = updatedQuestions[dragIndex];
    const targetUpdatedItem = updatedQuestions[dropIndex];

    // Update display order
    const finalQuestionData = updatedQuestions.map((item, index) => ({
      ...item,
      displayOrder: item?.displayOrder,
    }));

    props.setPreshiftQuestions(finalQuestionData);

    setDragIndex(null);
    setHoverIndex(null);

    let newDisplayOrder;
    if (targetItem) {
      newDisplayOrder = targetItem.displayOrder;
    } else {
      newDisplayOrder = cloneUpdatedQuestions[dropIndex - 1].displayOrder;
    }

    // API update Display Order
    // API call can be made here to update the display order in backend using finalQuestionData

    if (draggedItem?.displayOrder !== newDisplayOrder) {
      let params = {
        newDisplayOrder,
      }

      dispatch({ type: UPDATE_PRESHIFT_QUESTION_ORDER });
      API.put(`drivers/preshiftQuestions/${currentDraggedId}/dragDrop`, params)
        .then((response) => {
          dispatch({
            type: UPDATE_PRESHIFT_QUESTION_ORDER_SUCCESS,
          });
          props?.setDetectedChanges(false);
          getListOfPreshiftQuestions(props?.search);
        })
        .catch((err) => {
          dispatch({ type: UPDATE_PRESHIFT_QUESTION_ORDER_FAILURE, payload: err });
          toast.error(err?.response?.data?.message);
        });
    }
    
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setHoverIndex(null);
    draggingEnabled.current = false;
  };

  /*Handle Save Changes */
  const handleOpenSaveChangesPopup = () => {
    setOpenSaveChangeConfirmPopup(true);
    setError("");
  };
  const handleSaveChanges = (closeModal = false) => {
    dispatch({ type: SAVE_PRESHIFT_QUESTION_CHANGES });
    API.post("drivers/preshiftQuestions/saveChanges")
      .then((response) => {
        dispatch({
          type: SAVE_PRESHIFT_QUESTION_CHANGES_SUCCESS,
        });
        props?.setDetectedChanges(false);
        toast.success(
          <div style={{ whiteSpace: "pre-line" }}>
            {response?.data?.message}
          </div>);
        if (closeModal) {
          setOpenSaveDiscardChangePopup(false);
          props?.setOpen(false);
          return;
        }
        setOpenSaveChangeConfirmPopup(false);
        setOpenSaveDiscardChangePopup(false);
        getListOfPreshiftQuestions(props.search);
      })
      .catch((error) => {
        dispatch({ type: SAVE_PRESHIFT_QUESTION_CHANGES_FAILURE });
        setError(error?.response?.data?.message);
      });
  };
  
  const handleDiscardChanges = () => {
    dispatch({ type: DISCARD_PRESHIFT_QUESTION_CHANGES });
    API.post("drivers/preshiftQuestions/discardChanges")
      .then((response) => {
        dispatch({
          type: DISCARD_PRESHIFT_QUESTION_CHANGES_SUCCESS,
        });
        props?.setDetectedChanges(false);
        setOpenSaveDiscardChangePopup(false);
        getListOfPreshiftQuestions(props.search);
      })
      .catch((error) => {
        dispatch({ type: DISCARD_PRESHIFT_QUESTION_CHANGES_FAILURE });
        setError(error?.response?.data?.message);
      });    
  }
  
  return (
    <>
      <Dialog
        open={props.open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.customModal}
        scroll={scroll}
      >
        <div className="close-modal">
          <img src={closeIcon} alt="Close" onClick={() => props?.handleClose(props?.isSavedChangeEnabled)} />
        </div>
        <DialogTitle id="alert-dialog-title">
          Preshift Question
        </DialogTitle>
        {state.driver.gettingDriverById === true ? (
          <CircularProgress
            color="inherit"
            size={80}
            className="cirularProgess"
          />
        ) : (
          <DialogContent>
            <div className={classes.PreshiftPopupWrapper}>
              <div className="inner-wrapper">
                <div className="search-wrapper">
                  <div className="form-gourp">
                    <TextField
                      id="search-questionTxt"
                      placeholder="Search text"
                      variant="outlined"
                      type="search"
                      InputProps={{
                        endAdornment: (
                          <img src={SearchIcon} alt="Search" />
                        ),
                      }}
                      onChange={handleSearch}
                      value={props.search}
                    />
                  </div>
                </div>
                <div className="btn-group">
                  <div className="btn-wrapper">
                    <Button
                      className="orange-btn primary-btn"
                      color="inherit"
                      disableElevation
                      underlinenone="true"
                      onClick={() => handleAddEditQuestionPopup()}
                    >
                      + Add Question
                    </Button>

                    {/* Open Add Question Modal */}
                  </div>
                  <div className="btn-wrapper">
                    <Button
                      className={props?.isSavedChangeEnabled ? "blue-btn primary-btn" : "primary-btn"}
                      color="inherit"
                      disableElevation
                      underlinenone="true"
                      disabled={!props?.isSavedChangeEnabled}
                      onClick={() => handleOpenSaveChangesPopup()}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>

              <br/>
              <PreshiftTable
                handleDelete={(question) => handleDeleteQuestionPopup(question)}
                handleEdit={(question) => handleAddEditQuestionPopup(question)}
                order={props.order}
                orderBy={props.orderBy}
                handleSorting={(e, property) =>
                  handleSorting(e, property)
                }
                preshiftQuestions={props.preshiftQuestions}
                setPreshiftQuestions={props.setPreshiftQuestions}
                getListOfPreshiftQuestions={getListOfPreshiftQuestions}
                search={props.search}
                dragIndex={dragIndex}
                handleDragStart={handleDragStart}
                handleDragOver={handleDragOver}
                handleDrop={handleDrop}
                handleDragEnd={handleDragEnd}
                draggingEnabled={draggingEnabled}
              />

              <AddEditPreshiftPopup
                open={openAddEditQuestionPopup}
                handleClose={handleCloseAddEditQuestionModal}
                questionData={addEditQuestionData}
                formik={formik}
                error={error}
                isEdit={isEditMode}
              />

              <CustomDeletePopup
                open={openDeletePopup}
                handleClose={() => setOpenDeletePopup(false)}
                handleDelete={() => handleDeleteQuestion(deletedQuestionData)}
                deletedMsg={`Are you sure want to delete this question?`}
                loading={state?.driver?.deletingPreshiftQuestion}
                disabled={state?.driver?.deletingPreshiftQuestion}
                error={error}
              />

              <CustomConfirmationPopup
                open={openSaveChangeConfirmPopup}
                handleClose={() => setOpenSaveChangeConfirmPopup(false)}
                handleConfirm={() => handleSaveChanges(false)}
                title={"Confirmation"}
                message={"Are you sure you want to update the collection for the selected questions?"}
                closeMessageBtn={"Cancel"}
                confirmMessageBtn={"Yes"}
                loading={state?.driver?.savingPreshiftQuestionChanges}
                disabled={state?.driver?.savingPreshiftQuestionChanges}
                error={error}
              />
              
              <CustomConfirmationPopup
                open={openSaveDiscardChangePopup}
                handleClose={handleDiscardChanges}
                handleConfirm={() => handleSaveChanges(true)}
                title={"Confirmation"}
                message={"You have unsaved changes. Would you like to save before continuing?"}
                closeMessageBtn={"Discard"}
                confirmMessageBtn={"Save & Close"}
                loading={state?.driver?.savingPreshiftQuestionChanges}
                loadingCancel={state?.driver?.discardingPreshiftQuestionChanges}
                disabled={state?.driver?.savingPreshiftQuestionChanges || state?.driver?.discardingPreshiftQuestionChanges}
                error={error}
                hideCloseIcon={true}
              >
              </CustomConfirmationPopup>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}

export default PreshiftPopup;
